import * as React from 'react';
import { styled } from '@linaria/react';

import useTickSfx from '@/hooks/use-tick-sfx';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Space from '@/components/Space';

import { Svg, PositionDot } from './BasicShapes.shared';
import { getSymmetricalPolygonPoints } from './BasicShapes.helpers';

const SIZE = 280;

function PolygonFamilyDemo() {
  const stopSignVisits = React.useRef(0);
  const previousNumOfSides = React.useRef(0);
  const playTick = useTickSfx();

  return (
    <Demo
      id="basic-polyline-demo"
      outputWrapper={OutputWrapper}
      initialValues={{
        numOfSides: 6,
      }}
      CustomGridComponent={ControlsGrid}
      controls={[
        <DemoSliderControl
          key="numOfSides"
          id="numOfSides"
          label="# of sides"
          min={3}
          max={12}
          step={1}
          onChange={() => {
            playTick();
          }}
        />,
      ]}
    >
      {({ numOfSides }) => {
        const cx = SIZE / 2;
        const cy = SIZE / 2;
        const r = 80;

        const points = getSymmetricalPolygonPoints(
          numOfSides,
          cx,
          cy,
          r
        );

        const innerStopPoints = getSymmetricalPolygonPoints(
          8,
          cx,
          cy,
          r - 4
        );

        if (numOfSides === 8 && previousNumOfSides.current !== 8) {
          stopSignVisits.current++;
        }
        previousNumOfSides.current = numOfSides;

        const isStopSign =
          numOfSides === 8 && stopSignVisits.current <= 3;

        return (
          <Row>
            <SvgWrapper>
              <Svg size={SIZE}>
                {/* MAIN SHAPE: */}
                <Polygon
                  points={points}
                  stroke={
                    isStopSign
                      ? 'red'
                      : 'var(--wham-complementary-500)'
                  }
                  fill={isStopSign ? 'red' : 'transparent'}
                  strokeWidth={5}
                />

                <PositionDot
                  cx={cx}
                  cy={cy}
                  style={{ opacity: isStopSign ? 0 : 1 }}
                />

                <StopText
                  x={cx}
                  y={cy + 4}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ opacity: isStopSign ? 1 : 0 }}
                >
                  STOP
                </StopText>
                <Polygon
                  points={innerStopPoints}
                  fill="transparent"
                  stroke="white"
                  strokeWidth={4}
                  strokeLinejoin="round"
                  style={{ opacity: isStopSign ? 1 : 0 }}
                />
              </Svg>
            </SvgWrapper>
          </Row>
        );
      }}
    </Demo>
  );
}

const OutputWrapper = styled(Space)`
  container-type: inline-size;
  background: var(--color-background);
`;

const ControlsGrid = styled.div`
  display: grid;
  grid-template-columns: min(${SIZE + 6}px, 100%);
  justify-content: center;
`;

const Row = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

const SvgWrapper = styled.div`
  display: flex;

  @container (max-width: 37rem) {
    position: relative;
  }
`;

const Polygon = styled.polygon`
  transition: all 200ms;
`;

const StopText = styled.text`
  font-size: 2.5rem;
  font-weight: bold;
  fill: white;
  font-family: var(--font-family);
  transition: all 300ms;
`;

export default PolygonFamilyDemo;
